import React, { useState } from "react";
import { Link } from "react-router-dom";
import Logo from "../../Assets/img/LandingPageLogo.png";
import LandingPageBackground from "../../Assets/img/LandingPageBackground.jpg";
import { motion, AnimatePresence } from "framer-motion";

const LandingPage = () => {
  const [bgLoaded, setBgLoaded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
      className="relative flex items-center justify-center w-full h-screen overflow-hidden bg-[#0a0a0a]"
    >
      {/* Background Image: Premium Pan and Scale */}
      <AnimatePresence>
        {!bgLoaded && (
          <motion.div 
            exit={{ opacity: 0 }} 
            className="absolute inset-0 z-0 bg-[#0a0a0a] animate-pulse"
          />
        )}
      </AnimatePresence>

      <motion.img 
        src={LandingPageBackground}
        onLoad={() => setBgLoaded(true)}
        initial={{ opacity: 0, scale: 1.05 }}
        animate={{ 
          opacity: bgLoaded ? 0.6 : 0, 
          scale: bgLoaded ? 1 : 1.05 
        }}
        transition={{ 
          opacity: { duration: 2.5, ease: "easeOut" },
          scale: { duration: 3, ease: "easeOut" }
        }}
        className="absolute inset-0 object-cover w-full h-full z-0 pointer-events-none"
        alt="Sare Art Gallery"
      />

      {/* Uniform dark overlay and radial gradient to guarantee text contrast */}
      <div className="absolute inset-0 z-0 bg-black/40 pointer-events-none"></div>
      <div className="absolute inset-0 z-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-black/60 via-transparent to-transparent pointer-events-none"></div>

      {/* Main Content Area */}
      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 h-full flex flex-col justify-center drop-shadow-2xl">
        
        {/* Desktop Layout: Logo exactly in the center, links spread wide on left and right */}
        <div className="hidden md:flex items-center justify-between w-full mt-20">
          
          {/* Left Links */}
          <motion.div 
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1], delay: 0.8 }}
            className="flex flex-1 items-center justify-end gap-16 lg:gap-24 pr-10 lg:pr-20"
          >
            <Link 
              to="/main/home" 
              className="relative overflow-hidden group text-white/90 tracking-[0.4em] text-sm hover:text-white transition-colors duration-500"
            >
              HOME
              <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-white transition-all duration-500 group-hover:w-full"></span>
            </Link>
            <Link 
              to="/main/shop" 
              className="relative overflow-hidden group text-white/90 tracking-[0.4em] text-sm hover:text-white transition-colors duration-500"
            >
              SHOP
              <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-white transition-all duration-500 group-hover:w-full"></span>
            </Link>
          </motion.div>

          {/* Center Logo */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 2, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
            className="flex-shrink-0"
          >
            <img src={Logo} alt="Sare Art" className="w-56 lg:w-72 drop-shadow-2xl opacity-90 hover:opacity-100 transition-opacity duration-700" />
          </motion.div>

          {/* Right Links */}
          <motion.div 
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1], delay: 0.8 }}
            className="flex flex-1 items-center justify-start gap-16 lg:gap-24 pl-10 lg:pl-20"
          >
            <Link 
              to="/main/about" 
              className="relative overflow-hidden group text-white/90 tracking-[0.4em] text-sm hover:text-white transition-colors duration-500"
            >
              ABOUT
              <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-white transition-all duration-500 group-hover:w-full"></span>
            </Link>
            <a 
              href="https://www.instagram.com/sareabbasi7" 
              target="_blank"
              rel="noreferrer"
              className="relative overflow-hidden group text-white/90 tracking-[0.4em] text-sm hover:text-white transition-colors duration-500"
            >
              INSTA
              <span className="absolute bottom-0 left-0 w-0 h-[1px] bg-white transition-all duration-500 group-hover:w-full"></span>
            </a>
          </motion.div>

        </div>

        {/* Mobile Layout */}
        <div className="md:hidden flex flex-col items-center justify-center w-full h-full">
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
            className="mb-16"
          >
            <img src={Logo} alt="Sare Art" className="w-48 drop-shadow-2xl opacity-90" />
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1], delay: 0.8 }}
            className="flex flex-col items-center gap-8"
          >
            <Link to="/main/home" className="text-white/90 tracking-[0.4em] text-sm">HOME</Link>
            <Link to="/main/shop" className="text-white/90 tracking-[0.4em] text-sm">SHOP</Link>
            <Link to="/main/about" className="text-white/90 tracking-[0.4em] text-sm">ABOUT</Link>
            <a href="https://www.instagram.com/sareabbasi7" className="text-white/90 tracking-[0.4em] text-sm">INSTAGRAM</a>
          </motion.div>
        </div>

      </div>
    </motion.div>
  );
};

export default LandingPage;
